import { AxiosError, HttpStatusCode } from "axios";
import { Transcription } from "../types/transcription/Transcription";
import { getApi, postApi } from "./helpers/apiMethods";

export interface TranscribeRequest {
  customerId: string;
  interactionId: string;
}

export interface TranscribeResponse {
  jobId: string;
  status: string;
}

export async function initiateOrGetTranscription(
  customerId: string,
  interactionId: string,
): Promise<InitiateOrGetTranscriptionResponse> {
  try {
    const response = await getApi<InitiateOrGetTranscriptionResponse>(
      `/v1/customers/${customerId}/interactions/${interactionId}/transcription`,
    );
    if (!response.data) {
      throw new Error(
        "Unable to initiate or get transcription. Please check the customer ID and interaction ID.",
      );
    }
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      const response = error.response;
      if (response?.status === HttpStatusCode.Conflict) {
        throw new TranscriptionInProgressError();
      } else if (response?.status !== HttpStatusCode.Ok) {
        throw new Error(`Failed to initiate or get transcription.`);
      }
    }
    throw error;
  }
}

interface InitiateOrGetTranscriptionResponse {
  transcriptionJobName: string;
  transcribedUrl?: string;
}

export async function getTranscript(
  customerId: string,
  interactionId: string,
): Promise<Transcription | undefined> {
  const transcriptionUrl = await getTranscriptUrl(customerId, interactionId);
  const transcriptResponse = await fetch(transcriptionUrl);
  if (!transcriptResponse.ok) {
    throw new Error("The transcription exists but failed to load.");
  }
  return (await transcriptResponse.json()) as Transcription;
}

export async function getTranscriptUrl(
  customerId: string,
  interactionId: string,
): Promise<string> {
  const getTranscriptionResponse = await initiateOrGetTranscription(
    customerId,
    interactionId,
  );
  if (!getTranscriptionResponse.transcribedUrl) {
    throw new TranscriptionInProgressError();
  }
  return getTranscriptionResponse.transcribedUrl;
}

export enum TranscriptionStatus {
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
  IN_PROGRESS = "IN_PROGRESS",
  QUEUED = "QUEUED",
}

export class TranscriptionInProgressError extends Error {
  constructor() {
    super("Transcription is in progress. Please wait a moment and try again.");
    this.name = "TranscriptionInProgressError";
  }
}

export async function requestTranscript({
  customerId,
  interactionId,
}: TranscribeRequest): Promise<TranscribeResponse> {
  try {
    const response = await postApi<TranscribeResponse>(
      `/v1/customers/${customerId}/interactions/${interactionId}/transcription`,
      {},
    );
    if (!response.data) {
      throw new Error(
        "Unable to request transcript. Please check the customer ID and interaction ID.",
      );
    }
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      const response = error.response;
      if (response?.status === HttpStatusCode.Conflict) {
        throw new Error(
          "A transcription job is already in progress for this interaction.",
        );
      }
      throw new Error("Failed to request transcript.", error);
    }
    throw error;
  }
}
